import { Controller } from "@hotwired/stimulus";

const OPEN_KEY = "toc-sidebar-open";

export interface Heading {
	level: number;
	id: string;
	text: string;
}

export interface TocEntry extends Heading {
	children: TocEntry[];
}

export function buildTocTree(headings: Heading[]): TocEntry[] {
	const roots: TocEntry[] = [];
	let h2: TocEntry | undefined;
	let h3: TocEntry | undefined;

	for (const heading of headings) {
		if (!heading.id) continue;
		const entry: TocEntry = { ...heading, children: [] };

		if (heading.level === 2) {
			roots.push(entry);
			h2 = entry;
			h3 = undefined;
		} else if (heading.level === 3) {
			h2?.children.push(entry);
			h3 = entry;
		} else if (heading.level === 4) {
			(h3 ?? h2)?.children.push(entry);
		}
	}
	return roots;
}

export function openSectionId(tree: TocEntry[], activeId: string | null): string | undefined {
	const holds = (entry: TocEntry): boolean => entry.id === activeId || entry.children.some(holds);
	return (tree.find(holds) ?? tree[0])?.id;
}

interface RenderedToc {
	nav: HTMLElement;
	signature: string;
	list: HTMLOListElement;
	links: Map<string, HTMLElement>;
	sections: Map<string, HTMLElement>;
}

function renderToc(nav: HTMLElement, tree: TocEntry[], signature: string): RenderedToc {
	const links = new Map<string, HTMLElement>();
	const sections = new Map<string, HTMLElement>();

	const renderList = (entries: TocEntry[], className: string, depth: number): HTMLOListElement => {
		const ol = document.createElement("ol");
		ol.className = className;

		for (const entry of entries) {
			const li = document.createElement("li");
			const link = document.createElement("a");
			link.href = `#${entry.id}`;
			link.textContent = entry.text;
			link.className = "odo-link";
			link.dataset.tocId = entry.id;
			link.dataset.action = "click->toc#choose";
			links.set(entry.id, link);
			li.appendChild(link);

			if (entry.children.length > 0) {
				// An h4 straight under an h2 shares its list with the h3s that follow, which size it.
				const sub = renderList(entry.children, entry.children.some((child) => child.level === 3) ? "text-sm" : "text-xs", depth + 1);
				if (depth === 0) sections.set(entry.id, sub);
				li.appendChild(sub);
			}
			ol.appendChild(li);
		}
		return ol;
	};

	const list = renderList(tree, "odo-toc mr-2 ml-3 mb-2 max-h-[500px] lg:max-h-[calc(100vh-10rem)] overflow-y-auto", 0);
	return { nav, signature, list, links, sections };
}

export default class extends Controller {
	static targets = ["nav", "content", "sidebar", "showButton", "current", "indexButton"];
	static values = {
		selector: { type: String, default: ".odo-markdown > h2, .odo-markdown > h3, .odo-markdown > h4" },
		collapse: { type: Boolean, default: false },
		title: { type: String, default: "" },
	};
	declare readonly navTargets: HTMLElement[];
	declare readonly contentTarget: HTMLElement;
	declare readonly sidebarTarget: HTMLElement;
	declare readonly hasSidebarTarget: boolean;
	declare readonly showButtonTarget: HTMLElement;
	declare readonly hasShowButtonTarget: boolean;
	declare readonly currentTarget: HTMLElement;
	declare readonly hasCurrentTarget: boolean;
	declare readonly indexButtonTarget: HTMLButtonElement;
	declare readonly hasIndexButtonTarget: boolean;
	declare readonly selectorValue: string;
	declare readonly collapseValue: boolean;
	declare readonly titleValue: string;
	open = true;
	headers: HTMLElement[] = [];
	tree: TocEntry[] = [];
	rendered: RenderedToc[] = [];
	activeId: string | null = null;
	frameQueued = false;
	morphQueued = false;
	morphFrame = 0;
	// turbo:morph-element fires for every element Turbo morphs under this one; one rebuild covers them all.
	morphHandler = () => {
		if (this.morphQueued) return;
		this.morphQueued = true;
		this.morphFrame = requestAnimationFrame(() => {
			this.morphQueued = false;
			this.#build();
		});
	};
	scrollHandler = () => {
		if (this.frameQueued) return;
		this.frameQueued = true;
		requestAnimationFrame(() => {
			this.frameQueued = false;
			this.#sync();
		});
	};

	connect() {
		this.#build();
		window.addEventListener("scroll", this.scrollHandler, { passive: true });
		this.element.addEventListener("turbo:morph-element", this.morphHandler);
	}

	disconnect() {
		window.removeEventListener("scroll", this.scrollHandler);
		if (this.morphQueued) cancelAnimationFrame(this.morphFrame);
		this.morphQueued = false;
		this.element.removeEventListener("turbo:morph-element", this.morphHandler);
	}

	toggle() {
		this.open = !this.open;
		localStorage.setItem(OPEN_KEY, String(this.open));
		this.#updateUI();
	}

	choose(event: Event) {
		this.activate((event.currentTarget as HTMLElement).dataset.tocId ?? null);
	}

	// Renders the nav targets whose list is missing or stale and returns them; a list this controller still holds for the same headings stays.
	buildToc(): RenderedToc[] {
		const signature = JSON.stringify(this.tree);
		const previous = new Map(this.rendered.map((rendered) => [rendered.nav, rendered]));
		const fresh: RenderedToc[] = [];
		this.rendered = this.navTargets.map((nav) => {
			const old = previous.get(nav);
			if (old && old.signature === signature && nav.children.length === 1 && nav.children[0] === old.list) return old;

			const rendered = renderToc(nav, this.tree, signature);
			nav.replaceChildren(rendered.list);
			if (old) this.#restore(old, rendered);
			fresh.push(rendered);
			return rendered;
		});
		return fresh;
	}

	activate(id: string | null, lists: RenderedToc[] = this.rendered) {
		this.activeId = id;
		this.#mark();

		for (const { list, links } of lists) {
			const active = id === null ? undefined : links.get(id);
			if (!active) continue;
			const listRect = list.getBoundingClientRect();
			const activeRect = active.getBoundingClientRect();
			if (activeRect.top < listRect.top || activeRect.bottom > listRect.bottom) {
				list.scrollTo({ top: active.offsetTop - list.offsetTop - list.clientHeight / 2, behavior: "smooth" });
			}
		}
	}

	// A replaced list keeps the reader's place: its scroll position, and the focus when it was on one of its links.
	#restore(old: RenderedToc, rendered: RenderedToc) {
		rendered.list.scrollTop = old.list.scrollTop;
		for (const [id, link] of old.links) {
			if (link === document.activeElement) rendered.links.get(id)?.focus();
		}
	}

	// The last heading above a line 30% down the viewport; at the page's end the last one, which may never reach it.
	#sync(lists?: RenderedToc[]) {
		const line = window.innerHeight * 0.3;
		const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
		let current: HTMLElement | undefined;
		for (const header of this.headers) {
			if (!header.id) continue;
			if (!atEnd && header.getBoundingClientRect().top > line) break;
			current = header;
		}
		const id = current?.id ?? null;
		if (lists) this.activate(id, lists);
		else if (id !== this.activeId) this.activate(id);
	}

	#mark() {
		const openId = this.collapseValue ? openSectionId(this.tree, this.activeId) : undefined;

		for (const { links, sections } of this.rendered) {
			for (const [id, link] of links) {
				if (id === this.activeId) link.setAttribute("aria-current", "location");
				else link.removeAttribute("aria-current");
			}
			if (!this.collapseValue) continue;
			for (const [id, sub] of sections) sub.classList.toggle("hidden", id !== openId);
		}

		if (this.hasCurrentTarget) {
			const active = this.rendered[0]?.links.get(this.activeId ?? "");
			this.currentTarget.textContent = active?.textContent ?? this.titleValue;
		}
	}

	// Everything the page renders from the content: runs on connect and again after a Turbo morph, which restores the server's markup.
	#build() {
		this.#loadState();
		this.headers = [...this.contentTarget.querySelectorAll<HTMLElement>(this.selectorValue)];
		this.tree = buildTocTree(this.headers.map((header) => ({ level: Number(header.tagName.slice(1)), id: header.id, text: header.textContent?.trim() ?? "" })));
		if (this.hasIndexButtonTarget) this.indexButtonTarget.disabled = this.tree.length === 0;

		if (this.tree.length > 0) {
			this.#sync(this.buildToc());
		} else {
			this.rendered = [];
			for (const nav of this.navTargets) nav.innerHTML = "";
		}
	}

	#loadState() {
		const saved = localStorage.getItem(OPEN_KEY);
		this.open = saved !== null ? saved === "true" : true;
		this.#updateUI();
	}

	#updateUI() {
		if (!this.hasSidebarTarget) return;

		this.sidebarTarget.classList.toggle("lg:hidden", !this.open);
		if (this.hasShowButtonTarget) this.showButtonTarget.classList.toggle("lg:block", !this.open);
	}
}
